// materialMatching.ts

import {
  AmountYield,
  MixingWork,
  ElementWithMaterialDetails,
  Material,
  ElectrodeType,
  MaterialType,
} from '@twinsketch/topika-model';

/**
 * Material matching using character similarity and context
 */

interface MaterialIdentifier {
  materialName: string;
  modelName?: string;
  materialType?: string;
  activeMaterialCategory?: string;
  normalizedName: string;
  category: MaterialType;
  sourceType: 'formulation' | 'materialsNeeded' | 'mixingWork';
  electrodeType?: ElectrodeType;
}

interface MaterialMatchResult {
  matched: boolean;
  confidence: number; // 0-100
  reason?: string;
  mappedCategory?: MaterialType;
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Calculate similarity percentage between two strings (0-100)
 */
function stringSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  if (str1 === str2) return 100;

  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  const distance = levenshteinDistance(longer, shorter);
  const similarity = ((longer.length - distance) / longer.length) * 100;

  return Math.round(similarity);
}

/**
 * Calculate fuzzy match score considering multiple factors
 */
function calculateFuzzyMatchScore(str1: string, str2: string): number {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  // Exact match
  if (s1 === s2) return 100;

  // Check if one contains the other
  if (s1.includes(s2) || s2.includes(s1)) {
    const shorter = s1.length < s2.length ? s1 : s2;
    const longer = s1.length >= s2.length ? s1 : s2;
    return Math.round((shorter.length / longer.length) * 95);
  }

  // Check for common tokens (word-based matching)
  const tokens1 = s1.split(/[\s\-_.,]+/).filter(t => t.length > 1);
  const tokens2 = s2.split(/[\s\-_.,]+/).filter(t => t.length > 1);

  let matchingTokens = 0;
  let totalTokens = Math.max(tokens1.length, tokens2.length);

  for (const t1 of tokens1) {
    for (const t2 of tokens2) {
      const tokenSimilarity = stringSimilarity(t1, t2);
      if (tokenSimilarity > 80) {
        matchingTokens++;
        break;
      }
    }
  }

  if (totalTokens > 0) {
    const tokenScore = (matchingTokens / totalTokens) * 90;
    if (tokenScore > 50) return Math.round(tokenScore);
  }

  // Fall back to character-level similarity
  return stringSimilarity(s1, s2);
}

/**
 * Extract material codes/model numbers from text
 */
function extractMaterialCodes(text: string): string[] {
  // Match patterns like: 4286, NCA, KFW1100, BP2000, KS6, etc.
  const patterns = [
    /\b([A-Z]{2,}[\d]{2,})\b/g, // e.g., KFW1100, BP2000
    /\b([\d]{4})\b/g, // e.g., 4286
    /\b([A-Z]{2,6})\b/g, // e.g., NCA, PVDF, CMC, SBR
  ];
  const codes: string[] = [];
  patterns.forEach(pattern => {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      if (match[1] && !codes.includes(match[1])) {
        codes.push(match[1]);
      }
    }
  });
  return codes;
}

/**
 * Normalize material name with minimal preprocessing
 */
function normalizeMaterialName(name: string): string {
  if (!name) return '';

  return name.toLowerCase().trim().replace(/\s+/g, ' ').replace(/[\.]/g, ''); // Remove periods from O.A. -> oa
}

/**
 * Infer category from material name and context
 */
function inferCategoryFromName(name: string): MaterialType {
  const normalized = normalizeMaterialName(name);

  // Korean keywords
  if (normalized.includes('활물질')) return MaterialType.ACTIVE_MATERIAL;
  if (normalized.includes('도전재')) return MaterialType.CONDUCTING_AGENT;
  if (normalized.includes('바인더') || normalized.includes('binder')) return MaterialType.BINDER;

  // Solvents
  if (normalized.includes('nmp') || normalized.includes('diw') || normalized.includes('물')) {
    return MaterialType.SOLVENT;
  }

  // Known material patterns
  if (/kfw|pvdf|cmc|sbr/.test(normalized)) {
    return MaterialType.BINDER;
  }

  if (/super-?p|ks6|bp\d{4}|carbon\s*black/.test(normalized)) {
    return MaterialType.CONDUCTING_AGENT;
  }

  if (/nca|nmc|lco|lmo|lfp|graphite/.test(normalized) || /^\d{4}$/.test(normalized)) {
    return MaterialType.ACTIVE_MATERIAL;
  }

  if (/\boa\b|oxalic/.test(normalized)) {
    return MaterialType.ADDITIVE;
  }

  // Current collectors
  if (normalized.includes('foil') || /\b(al|cu)\s/.test(normalized)) {
    return MaterialType.CURRENT_COLLECTOR;
  }
  return MaterialType.UNKNOWN;
}

/**
 * Get material identifier from formulation element
 */
export function getFormulationMaterialIdentifier(
  element: ElementWithMaterialDetails,
  electrodeType?: ElectrodeType
): MaterialIdentifier {
  const material = element.material;
  const name = material.materialName || material.modelName || '';

  return {
    materialName: name,
    modelName: material.modelName,
    materialType: material.materialType,
    activeMaterialCategory: material.activeMaterialCategory,
    normalizedName: normalizeMaterialName(name),
    category: categorizeMaterial(material),
    sourceType: 'formulation',
    electrodeType,
  };
}

function categorizeMaterial(material: Material): MaterialType {
  const materialType = material.materialType;

  if (materialType === 'Active Material') return MaterialType.ACTIVE_MATERIAL;
  if (materialType === 'Binder') return MaterialType.BINDER;
  if (materialType === 'Conducting Agent') return MaterialType.CONDUCTING_AGENT;
  if (materialType === 'Solvent') return MaterialType.SOLVENT;
  if (materialType === 'Additive') return MaterialType.ADDITIVE;
  if (materialType === 'Current Collector') return MaterialType.CURRENT_COLLECTOR;

  return MaterialType.UNKNOWN;
}

/**
 * Get material identifier from materialsNeeded
 */
export function getMaterialsNeededIdentifier(
  materialNeeded: AmountYield,
  electrodeType?: ElectrodeType,
  categoryOverride?: MaterialType
): MaterialIdentifier {
  return {
    materialName: materialNeeded.materialName,
    normalizedName: normalizeMaterialName(materialNeeded.materialName),
    category: categoryOverride ?? inferCategoryFromName(materialNeeded.materialName),
    sourceType: 'materialsNeeded',
    electrodeType,
  };
}

/**
 * Get material identifier from mixing work step
 */
export function getMixingWorkMaterialIdentifier(
  stepMaterialName: string,
  electrodeType?: ElectrodeType
): MaterialIdentifier {
  const codes = extractMaterialCodes(stepMaterialName);

  return {
    materialName: stepMaterialName,
    modelName: codes.length > 0 ? codes[0] : undefined,
    normalizedName: normalizeMaterialName(stepMaterialName),
    category: inferCategoryFromName(stepMaterialName),
    sourceType: 'mixingWork',
    electrodeType,
  };
}

/**
 * Match two materials using similarity scoring
 */
export function matchMaterials(
  material1: MaterialIdentifier,
  material2: MaterialIdentifier
): MaterialMatchResult {
  let maxScore = 0;
  let matchReason = '';

  // 1. Exact normalized name match
  if (material1.normalizedName === material2.normalizedName) {
    return {
      matched: true,
      confidence: 100,
      reason: 'Exact name match',
    };
  }

  // 2. Model name/code matching
  if (material1.modelName && material2.modelName) {
    const modelScore = calculateFuzzyMatchScore(material1.modelName, material2.modelName);
    if (modelScore > maxScore) {
      maxScore = modelScore;
      matchReason = 'Model/code match';
    }
  }

  // Check model code against full name
  if (material1.modelName) {
    const codes2 = extractMaterialCodes(material2.materialName);
    for (const code of codes2) {
      const score = calculateFuzzyMatchScore(material1.modelName, code);
      if (score > maxScore) {
        maxScore = score;
        matchReason = 'Embedded code match';
      }
    }
  }

  if (material2.modelName) {
    const codes1 = extractMaterialCodes(material1.materialName);
    for (const code of codes1) {
      const score = calculateFuzzyMatchScore(material2.modelName, code);
      if (score > maxScore) {
        maxScore = score;
        matchReason = 'Embedded code match';
      }
    }
  }

  // 3. Name similarity
  const nameScore = calculateFuzzyMatchScore(material1.normalizedName, material2.normalizedName);
  if (nameScore > maxScore) {
    maxScore = nameScore;
    matchReason = 'Name similarity';
  }

  // 4. Category-based matching with context
  if (material1.category === material2.category && material1.category !== MaterialType.UNKNOWN) {
    // Boost score for matching categories
    const categoryBonus = 20;
    const boostedScore = Math.min(100, maxScore + categoryBonus);

    // Special handling for generic categories with electrode context
    if (
      material1.category === MaterialType.ACTIVE_MATERIAL &&
      material1.sourceType === 'mixingWork'
    ) {
      // "활물질" should match any active material in the same electrode type
      return {
        matched: true,
        confidence: 85,
        reason: 'Active material category match',
        mappedCategory: MaterialType.ACTIVE_MATERIAL,
      };
    }

    if (material1.category === MaterialType.SOLVENT) {
      // Context-aware solvent matching
      const isNMP1 = material1.normalizedName.includes('nmp');
      const isNMP2 = material2.normalizedName.includes('nmp');
      const isDIW1 =
        material1.normalizedName.includes('diw') || material1.normalizedName.includes('물');
      const isDIW2 =
        material2.normalizedName.includes('diw') || material2.normalizedName.includes('water');

      if ((isNMP1 && isNMP2) || (isDIW1 && isDIW2)) {
        return {
          matched: true,
          confidence: 95,
          reason: 'Solvent type match',
        };
      }
    }

    if (boostedScore > maxScore) {
      maxScore = boostedScore;
      matchReason = 'Category match with similarity';
    }
  }

  // 5. Solution matching (special case)
  if (isSolutionMatch(material1, material2)) {
    const solutionScore = 90;
    if (solutionScore > maxScore) {
      maxScore = solutionScore;
      matchReason = 'Solution composition match';
    }
  }

  // Decision threshold
  const threshold = 70;
  return {
    matched: maxScore >= threshold,
    confidence: maxScore,
    reason: matchReason,
  };
}

/**
 * Check if materials represent the same solution
 */
function isSolutionMatch(mat1: MaterialIdentifier, mat2: MaterialIdentifier): boolean {
  const hasSolution1 = mat1.normalizedName.includes('sol') || mat1.category === MaterialType.BINDER;
  const hasSolution2 = mat2.normalizedName.includes('sol') || mat2.category === MaterialType.BINDER;

  if (!hasSolution1 && !hasSolution2) return false;

  // Extract numeric codes from both
  const codes1 = extractMaterialCodes(mat1.materialName);
  const codes2 = extractMaterialCodes(mat2.materialName);

  // Check for matching codes
  for (const code1 of codes1) {
    for (const code2 of codes2) {
      if (calculateFuzzyMatchScore(code1, code2) > 80) {
        return true;
      }
    }
  }

  return false;
}
/**
 * Find best matching material from materialsNeeded for a mixing work step
 */
export function findBestMatch(
  mixingStepName: string,
  materialsNeeded: AmountYield[],
  electrodeType?: ElectrodeType,
  formulationElements?: ElementWithMaterialDetails[],
  materialNeededWithCategory?: AmountYieldWithCategory[]
): { material: AmountYield; confidence: number } | null {
  const stepIdentifier = getMixingWorkMaterialIdentifier(mixingStepName, electrodeType);

  let bestMatch: AmountYield | null = null;
  let bestConfidence = 0;

  // Use materialNeededWithCategory if available for enhanced matching
  if (materialNeededWithCategory && materialNeededWithCategory.length > 0) {
    for (let i = 0; i < materialNeededWithCategory.length; i++) {
      const neededWithCat = materialNeededWithCategory[i];
      const needed = materialsNeeded[i];

      let currentConfidence = 0;
      let matchReason = '';

      // 1. Try matching with materialName from materialsNeeded
      const materialNameScore = calculateFuzzyMatchScore(
        mixingStepName,
        neededWithCat.materialName
      );
      if (materialNameScore > currentConfidence) {
        currentConfidence = materialNameScore;
        matchReason = 'materialName match';
      }

      // 2. Try matching with materialNameFromFormulation
      if (neededWithCat.materialNameFromFormulation) {
        const formMaterialNameScore = calculateFuzzyMatchScore(
          mixingStepName,
          neededWithCat.materialNameFromFormulation
        );
        if (formMaterialNameScore > currentConfidence) {
          currentConfidence = formMaterialNameScore;
          matchReason = 'materialNameFromFormulation match';
        }
      }

      // 3. Try matching with modelNameFromFormulation
      if (neededWithCat.modelNameFromFormulation) {
        const formModelNameScore = calculateFuzzyMatchScore(
          mixingStepName,
          neededWithCat.modelNameFromFormulation
        );
        if (formModelNameScore > currentConfidence) {
          currentConfidence = formModelNameScore;
          matchReason = 'modelNameFromFormulation match';
        }

        // Also check if model name is embedded in step name
        const stepCodes = extractMaterialCodes(mixingStepName);
        for (const code of stepCodes) {
          const codeScore = calculateFuzzyMatchScore(code, neededWithCat.modelNameFromFormulation);
          if (codeScore > currentConfidence) {
            currentConfidence = codeScore;
            matchReason = 'embedded model code match';
          }
        }
      }

      // 4. Category-based matching
      if (
        stepIdentifier.category === neededWithCat.category &&
        neededWithCat.category !== MaterialType.UNKNOWN
      ) {
        // Boost score for matching categories
        const categoryBonus = 20;
        const boostedScore = Math.min(100, currentConfidence + categoryBonus);

        // Special handling for generic material names like "활물질", "도전재", etc.
        if (
          stepIdentifier.category === MaterialType.ACTIVE_MATERIAL &&
          stepIdentifier.sourceType === 'mixingWork'
        ) {
          // If mixing step just says "활물질" and there's only one active material
          const activeMatCount = materialNeededWithCategory.filter(
            m => m.category === MaterialType.ACTIVE_MATERIAL
          ).length;

          if (activeMatCount === 1 && boostedScore < 85) {
            currentConfidence = 85;
            matchReason = 'single active material category match';
          }
        }

        if (boostedScore > currentConfidence) {
          currentConfidence = boostedScore;
          matchReason = 'category match with name similarity';
        }
      }

      // Update best match if this is better
      if (currentConfidence > bestConfidence) {
        bestMatch = needed;
        bestConfidence = currentConfidence;
        console.log(
          `Best match updated: ${matchReason} (${currentConfidence}%) for "${mixingStepName}" -> "${needed.materialName}"`
        );
      }
    }
  } else {
    // Fallback to original logic if materialNeededWithCategory not available
    for (const needed of materialsNeeded) {
      const neededIdentifier = getMaterialsNeededIdentifier(needed, electrodeType);
      const matchResult = matchMaterials(stepIdentifier, neededIdentifier);

      if (matchResult.matched && matchResult.confidence > bestConfidence) {
        bestMatch = needed;
        bestConfidence = matchResult.confidence;
      }
    }

    // If still no good match, try through formulation
    if ((!bestMatch || bestConfidence < 80) && formulationElements) {
      for (const element of formulationElements) {
        const formIdentifier = getFormulationMaterialIdentifier(element, electrodeType);
        const matchResult = matchMaterials(stepIdentifier, formIdentifier);

        if (matchResult.matched && matchResult.confidence > bestConfidence) {
          const formMaterialName = element.material.materialName || element.material.modelName;
          const correspondingNeeded = materialsNeeded.find(
            n => calculateFuzzyMatchScore(n.materialName, formMaterialName || '') > 80
          );

          if (correspondingNeeded) {
            bestMatch = correspondingNeeded;
            bestConfidence = matchResult.confidence;
          }
        }
      }
    }
  }

  return bestMatch ? { material: bestMatch, confidence: bestConfidence } : null;
}

interface AmountYieldWithCategory {
  materialName: string;
  net: number;
  min: number;
  category: MaterialType;
  materialNameFromFormulation: string;
  modelNameFromFormulation: string;
}

/**
 * Calculate actual usage from mixing works with similarity matching
 */
export function calculateActualUsageFromMixingWorks(
  mixingWorks: MixingWork[],
  materialsNeeded: AmountYield[],
  electrodeType?: ElectrodeType,
  formulationElements?: ElementWithMaterialDetails[]
): Map<
  string,
  {
    totalAmount: number;
    breakdown: Array<{
      projectName: string;
      date: string;
      amount: number;
      stepName: string;
      confidence: number;
    }>;
    category: MaterialType;
    materialNeeded: AmountYield;
  }
> {
  const usageMap = new Map<
    string,
    {
      totalAmount: number;
      breakdown: Array<{
        projectName: string;
        date: string;
        amount: number;
        stepName: string;
        confidence: number;
      }>;
      category: MaterialType;
      materialNeeded: AmountYield;
    }
  >();

  // Filter out foil materials from materialsNeeded at the start
  const filteredMaterialsNeeded = materialsNeeded.filter(
    needed => !normalizeMaterialName(needed.materialName).includes('foil')
  );

  // expand existing materialsNeeded with category..
  const materialNeededWithCategory: AmountYieldWithCategory[] = [];
  filteredMaterialsNeeded.forEach(needed => {
    if (normalizeMaterialName(needed.materialName).includes('foil')) return;
    const identifier = getMaterialsNeededIdentifier(needed, electrodeType);

    // Try to get more accurate category from formulation if available
    let category = identifier.category;
    let materialNameFromFormulation = '';
    let modelNameFromFormulation = '';

    if (formulationElements) {
      const formElement = formulationElements.find(
        el =>
          (el.material.materialName || el.material.modelName) === needed.materialName ||
          calculateFuzzyMatchScore(
            el.material.materialName || el.material.modelName || '',
            needed.materialName
          ) > 85
      );

      if (formElement) {
        const formIdentifier = getFormulationMaterialIdentifier(formElement, electrodeType);
        category = formIdentifier.category;
        materialNameFromFormulation = formElement.material.materialName || '';
        modelNameFromFormulation = formElement.material.modelName || '';
      } else {
        // No direct match found - try to find materials with matching category
        const materialFromFormulationList = formulationElements.filter(
          el => el.material?.materialType === category
        );

        if (materialFromFormulationList.length === 1) {
          materialNameFromFormulation = materialFromFormulationList[0].material.materialName || '';
          modelNameFromFormulation = materialFromFormulationList[0].material.modelName || '';
        }
      }
    }

    // Build the AmountYieldWithCategory object
    materialNeededWithCategory.push({
      materialName: needed.materialName,
      net: needed.net,
      min: needed.min,
      category: category,
      materialNameFromFormulation: materialNameFromFormulation,
      modelNameFromFormulation: modelNameFromFormulation,
    });
  });

  // Process mixing works
  mixingWorks.forEach(work => {
    work.steps.forEach(step => {
      const match = findBestMatch(
        step.materialName,
        filteredMaterialsNeeded,
        electrodeType,
        formulationElements,
        materialNeededWithCategory
      );

      if (!match) {
        console.warn(`No reliable match found for mixing step: "${step.materialName}"`);
        return; // Skip this step
      }

      console.log(match.material.materialName, '/', step.materialName);

      const materialKey = match.material.materialName;

      // Get or create usage entry
      let usageEntry = usageMap.get(materialKey);
      if (!usageEntry) {
        // Find the category for this material
        const materialWithCat = materialNeededWithCategory.find(
          m => m.materialName === materialKey
        );

        usageEntry = {
          totalAmount: 0,
          breakdown: [],
          category: materialWithCat?.category || MaterialType.UNKNOWN,
          materialNeeded: match.material,
        };
        usageMap.set(materialKey, usageEntry);
      }

      // Add to breakdown
      usageEntry.breakdown.push({
        projectName: work.projectName,
        date: work.date,
        amount: step.amountInKg,
        stepName: step.materialName,
        confidence: match.confidence,
      });

      // Update total amount
      usageEntry.totalAmount += step.amountInKg;

      console.log(
        `Matched: "${step.materialName}" (${step.amountInKg}kg) -> "${match.material.materialName}" (confidence: ${match.confidence}%)`
      );
    });
  });

  return usageMap;
}
